import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
    connectionSoftwareOptions,
    booleanOptions
} from '../../config/dropdown-options.js';
import SubmitButton from '../common/SubmitButton.jsx';
import useOffices from '../../hooks/useOffices.js';
import { createOfficeFormFields } from '../../config/form-fields.js';
import createOfficeValidation from '../../validation/create-office-validation.js';
import ErrorMessage from '../common/ErrorMessage.jsx';
import SuccessMessage from '../common/SuccessMessage.jsx';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import { setOfficeObj } from '../../redux/slices/office-slice.js';
import convertPhoneNumberToNationalFormat from '../../utils/convert-phone-number-to-national.js';
import useUsers from '../../hooks/useUsers.js';
import { red } from '../../styles/standard-colors.js';

const isBlank = (value) => value == null || String(value).trim() === '';

/**
 * CreateUpdateOfficeForm component for rendering the form to create a new office or update an existing one.
 *
 * @component
 * @returns {JSX.Element} - The rendered CreateUpdateOfficeForm component.
 */
const CreateUpdateOfficeForm = ({ objectToUpdate, setShowEdit }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        error: officeError,
        loading: officeLoading,
        office,
        createOffice,
        updateOffice,
        getAllHubOffices
    } = useOffices();

    const {
        error: usersError,
        loading: usersLoading,
        getAllUsers
    } = useUsers();

    const [userDropdownOptions, setUserDropdownOptions] = useState([]);
    const [hubOfficeDropdownOptions, setHubOfficeDropdownOptions] = useState(
        []
    );

    if (objectToUpdate) {
        // eslint-disable-next-line no-param-reassign
        objectToUpdate = { ...objectToUpdate };
        // eslint-disable-next-line no-param-reassign
        objectToUpdate.phoneNumber = convertPhoneNumberToNationalFormat(
            objectToUpdate.phoneNumber
        );
        // eslint-disable-next-line no-param-reassign
        objectToUpdate.itPhoneNumber = convertPhoneNumberToNationalFormat(
            objectToUpdate.itPhoneNumber
        );
    }

    const formik = useFormik({
        initialValues: {
            ...createOfficeFormFields,
            ...objectToUpdate
        },
        validationSchema: createOfficeValidation,
        onSubmit: async (values) => {
            const { id, ...formValues } = values;
            [
                'dsUsername',
                'dsPassword',
                'dsUsername2',
                'dsPassword2',
                'eClaimsUsername',
                'eClaimsPassword',
                'practiceBoosterUsername',
                'practiceBoosterPassword',
                'sikkaUser',
                'sikkaPass',
                'copilotServerUsername',
                'copilotServerPassword',
                'edsPracticePassword',
                'edsPortalUser',
                'edsPortalPassword',
                'copilotPracticeIdentityUser',
                'copilotPracticeIdentityPassword',
                'openDentalApiKey',
                'mysqlUser',
                'mysqlPassword',
                'copilotInstallPassword',
                'superSyncCognitoPassword',
                'dxcUser',
                'dxcPassword',
                'nextCloudPassword',
                'nextCloudQrCodeImageObjectKey'
            ].forEach((field) => {
                delete formValues[field];
            });

            if (objectToUpdate) {
                const updatedOffice = await updateOffice(
                    objectToUpdate.id,
                    formValues,
                    objectToUpdate
                );
                if (updatedOffice) {
                    dispatch(setOfficeObj(updatedOffice));
                    setShowEdit(false);
                }
            } else {
                const newOffice = await createOffice(formValues);
                if (newOffice) {
                    navigate(`/office/${newOffice.id}`);
                }
            }
        }
    });

    const isCopilot = !!formik.values.isCopilotOffice;
    const isCompanion = !!formik.values.isCompanionOffice;
    const hasDxc = !isBlank(formik.values.dxcAccountNumber);
    const hasEds = !isBlank(formik.values.edsPracticeId);
    const billingIncomplete = isCopilot && !hasDxc && !hasEds;

    const requiredLabelStyle = (incomplete) =>
        incomplete ? { color: red } : undefined;

    const formatPhoneNumber = (value) => {
        if (!value) return value;

        // Remove all non-numeric characters
        const phoneNumber = value.replace(/[^\d]/g, '');

        const phoneNumberLength = phoneNumber.length;

        if (phoneNumberLength < 4) return phoneNumber;
        if (phoneNumberLength < 7) {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        }

        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
            3,
            6
        )}-${phoneNumber.slice(6, 10)}`;
    };

    useEffect(() => {
        /**
         * Checks if the error passed back from submission is because the office systematic name is taken.
         * If it is, sets the proper field error
         */
        const callCheckSysName = async () => {
            if (
                officeError &&
                officeError.message === 'Office Systematic Name already exists'
            ) {
                formik.setFieldError(
                    'officeSysName',
                    'That systematic name is taken, please select another'
                );
            }
        };

        callCheckSysName();
    }, [officeError]);

    useEffect(() => {
        const getUsers = async () => {
            let returnedUsers = await getAllUsers();
            returnedUsers = returnedUsers.map((u) => {
                // eslint-disable-next-line no-param-reassign
                u = {
                    value: u.email,
                    label: u.email
                };
                return u;
            });
            setUserDropdownOptions(returnedUsers);
        };

        const getHubOffices = async () => {
            try {
                const returnedHubOffices = await getAllHubOffices();

                const safeHubOffices = Array.isArray(returnedHubOffices)
                    ? [...returnedHubOffices]
                    : [];

                safeHubOffices.unshift({
                    value: null,
                    label: 'None'
                });

                if (
                    formik.values.hubId &&
                    !safeHubOffices.find((o) => o.value === formik.values.hubId)
                ) {
                    safeHubOffices.push({
                        value: formik.values.hubId,
                        label: `${formik.values.hubId} (Inactive)`
                    });
                }

                setHubOfficeDropdownOptions(safeHubOffices);
            } catch (err) {
                console.error('Failed to load hub offices:', err);
                setHubOfficeDropdownOptions([{ value: null, label: 'None' }]);
            }
        };

        getUsers();
        getHubOffices();
    }, []);

    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <table className="office-table">
                    <tbody>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="officeSysName">
                                    Office Systematic Name:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="officeSysName"
                                    type="text"
                                    className="office-computer-edit-input"
                                    style={{
                                        color: formik.errors.officeSysName
                                            ? 'red'
                                            : 'black'
                                    }}
                                    {...formik.getFieldProps('officeSysName')}
                                />
                            </td>
                            {formik.touched.officeSysName &&
                            formik.errors.officeSysName ? (
                                <ErrorMessage
                                    message={formik.errors.officeSysName}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="notes">Notes:</label>
                            </td>
                            <td className="office-table-right-cell">
                                <textarea
                                    id="notes"
                                    className="office-computer-edit-input"
                                    style={{
                                        height: '10em',
                                        paddingTop: '.5em'
                                    }}
                                    {...formik.getFieldProps('notes')}
                                />
                            </td>
                            {formik.touched.notes && formik.errors.notes ? (
                                <ErrorMessage message={formik.errors.notes} />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="hubId">
                                    Linked hub office:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                {officeLoading && <LoadingSpinner />}
                                {/* API error (hub not sending data) */}
                                {officeError && (
                                    <ErrorMessage message="⚠️ Unable to load hub offices. Please try again later." />
                                )}
                                {/* Dropdown (only usable when no API error) */}
                                {hubOfficeDropdownOptions.length > 0 && (
                                    <Select
                                        id="hubId"
                                        name="hubId"
                                        options={hubOfficeDropdownOptions}
                                        value={hubOfficeDropdownOptions.find(
                                            (option) =>
                                                option.value ===
                                                formik.values.hubId
                                        )}
                                        onChange={(selectedOption) =>
                                            formik.setFieldValue(
                                                'hubId',
                                                selectedOption
                                                    ? selectedOption.value
                                                    : null
                                            )
                                        }
                                    />
                                )}
                                {/* Inactive office warning (ONLY when data loaded successfully) */}
                                {!officeLoading &&
                                    !officeError &&
                                    formik.values.hubId &&
                                    hubOfficeDropdownOptions.length > 0 &&
                                    !hubOfficeDropdownOptions.some(
                                        (o) => o.value === formik.values.hubId
                                    ) && (
                                        <ErrorMessage message="⚠️ This office is inactive. Please select an active office." />
                                    )}
                            </td>
                            {formik.touched.hubId && formik.errors.hubId ? (
                                <ErrorMessage message={formik.errors.hubId} />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="officeName">Office Name:</label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="officeName"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('officeName')}
                                />
                            </td>
                            {formik.touched.officeName &&
                            formik.errors.officeName ? (
                                <ErrorMessage
                                    autofocus
                                    message={formik.errors.officeName}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="phoneNumber">
                                    Office Phone Number:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="phoneNumber"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('phoneNumber')}
                                    onChange={(e) => {
                                        formik.setFieldValue(
                                            'phoneNumber',
                                            formatPhoneNumber(e.target.value)
                                        );
                                    }}
                                />
                            </td>
                            {formik.touched.phoneNumber &&
                            formik.errors.phoneNumber ? (
                                <ErrorMessage
                                    message={formik.errors.phoneNumber}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="officeHours">
                                    Office Hours:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="officeHours"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('officeHours')}
                                />
                            </td>
                            {formik.touched.officeHours &&
                            formik.errors.officeHours ? (
                                <ErrorMessage
                                    message={formik.errors.officeHours}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="officeEmail">
                                    Office Email:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="officeEmail"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('officeEmail')}
                                />
                            </td>
                            {formik.touched.officeEmail &&
                            formik.errors.officeEmail ? (
                                <ErrorMessage
                                    message={formik.errors.officeEmail}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="poc">
                                    Point of contact (POC):
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="poc"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('poc')}
                                />
                            </td>
                            {formik.touched.poc && formik.errors.poc ? (
                                <ErrorMessage message={formik.errors.poc} />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="itName">IT Name:</label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="itName"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('itName')}
                                />
                            </td>
                            {formik.touched.itName && formik.errors.itName ? (
                                <ErrorMessage message={formik.errors.itName} />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="itEmail">IT Email:</label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="itEmail"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('itEmail')}
                                />
                            </td>
                            {formik.touched.itEmail && formik.errors.itEmail ? (
                                <ErrorMessage message={formik.errors.itEmail} />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="itPhoneNumber">
                                    IT Phone Number:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="itPhoneNumber"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('itPhoneNumber')}
                                    onChange={(e) => {
                                        formik.setFieldValue(
                                            'itPhoneNumber',
                                            formatPhoneNumber(e.target.value)
                                        );
                                    }}
                                />
                            </td>
                            {formik.touched.itPhoneNumber &&
                            formik.errors.itPhoneNumber ? (
                                <ErrorMessage
                                    message={formik.errors.itPhoneNumber}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="production">Production:</label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="production"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('production')}
                                />
                            </td>
                            {formik.touched.production &&
                            formik.errors.production ? (
                                <ErrorMessage
                                    message={formik.errors.production}
                                />
                            ) : null}
                        </tr>
                        {objectToUpdate && (
                            <>
                                <tr>
                                    <td className="office-table-left-cell">
                                        <label htmlFor="installTech">
                                            Install Tech:
                                        </label>
                                    </td>
                                    <td className="office-table-right-cell">
                                        {usersLoading && <LoadingSpinner />}
                                        {usersError && (
                                            <ErrorMessage
                                                message={usersError.message}
                                            />
                                        )}
                                        {userDropdownOptions.length > 0 && (
                                            <Select
                                                id="installTech"
                                                name="installTech"
                                                options={userDropdownOptions}
                                                value={userDropdownOptions.find(
                                                    (option) =>
                                                        option.value ===
                                                        formik.values
                                                            .installTech
                                                )}
                                                onChange={(selectedOption) =>
                                                    formik.setFieldValue(
                                                        'installTech',
                                                        selectedOption
                                                            ? selectedOption.value
                                                            : null
                                                    )
                                                }
                                            />
                                        )}
                                    </td>
                                    {formik.touched.installTech &&
                                    formik.errors.installTech ? (
                                        <ErrorMessage
                                            message={formik.errors.installTech}
                                        />
                                    ) : null}
                                </tr>
                                <tr>
                                    <td className="office-table-left-cell">
                                        <label htmlFor="installDate">
                                            Install Date:
                                        </label>
                                    </td>
                                    <td className="office-table-right-cell">
                                        <input
                                            id="installDate"
                                            type="date"
                                            className="office-computer-edit-input"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            defaultValue={
                                                formik.values.installDate
                                                    ? new Date(
                                                          formik.values.installDate
                                                      )
                                                          .toISOString()
                                                          .substring(0, 10)
                                                    : null
                                            }
                                        />
                                    </td>
                                    {formik.touched.installDate &&
                                    formik.errors.installDate ? (
                                        <ErrorMessage
                                            message={formik.errors.installDate}
                                        />
                                    ) : null}
                                </tr>
                            </>
                        )}

                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="numOfDBConnections">
                                    Number of DB Connections:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="numOfDBConnections"
                                    type="number"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps(
                                        'numOfDBConnections'
                                    )}
                                />
                            </td>
                            {formik.touched.numOfDBConnections &&
                            formik.errors.numOfDBConnections ? (
                                <ErrorMessage
                                    message={formik.errors.numOfDBConnections}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="numOfIVConnections">
                                    Number of IV Connections:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="numOfIVConnections"
                                    type="number"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps(
                                        'numOfIVConnections'
                                    )}
                                />
                            </td>
                            {formik.touched.numOfIVConnections &&
                            formik.errors.numOfIVConnections ? (
                                <ErrorMessage
                                    message={formik.errors.numOfIVConnections}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="numOfPPConnections">
                                    Number of PB Connections:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="numOfPPConnections"
                                    type="number"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps(
                                        'numOfPPConnections'
                                    )}
                                />
                            </td>
                            {formik.touched.numOfPPConnections &&
                            formik.errors.numOfPPConnections ? (
                                <ErrorMessage
                                    message={formik.errors.numOfPPConnections}
                                />
                            ) : null}
                        </tr>

                        {/* <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="numOfFSConnections">
                                    Number of FS Connections:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="numOfFSConnections"
                                    type="number"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps(
                                        'numOfFSConnections'
                                    )}
                                />
                            </td>
                            {formik.touched.numOfFSConnections &&
                            formik.errors.numOfFSConnections ? (
                                <ErrorMessage
                                    message={formik.errors.numOfFSConnections}
                                />
                            ) : null}
                        </tr> */}
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="eobPath">EOB Path:</label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="eobPath"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('eobPath')}
                                />
                            </td>
                            {formik.touched.eobPath && formik.errors.eobPath ? (
                                <ErrorMessage message={formik.errors.eobPath} />
                            ) : null}
                        </tr>

                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="connectionSoftware">
                                    Connection Software:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <Select
                                    id="connectionSoftware"
                                    name="connectionSoftware"
                                    options={connectionSoftwareOptions}
                                    value={connectionSoftwareOptions.find(
                                        (option) =>
                                            option.value ===
                                            formik.values.connectionSoftware
                                    )}
                                    onChange={(selectedOption) =>
                                        formik.setFieldValue(
                                            'connectionSoftware',
                                            selectedOption
                                                ? selectedOption.value
                                                : null
                                        )
                                    }
                                />
                            </td>
                            {formik.touched.connectionSoftware &&
                            formik.errors.connectionSoftware ? (
                                <ErrorMessage
                                    message={formik.errors.connectionSoftware}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="addendumOnFile">
                                    Unique VPN Addendum on File:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <Select
                                    id="addendumOnFile"
                                    name="addendumOnFile"
                                    options={booleanOptions}
                                    value={booleanOptions.find(
                                        (option) =>
                                            option.value ===
                                            formik.values.uniqueVPN
                                                .addendumOnFile
                                    )}
                                    onChange={(selectedOption) =>
                                        formik.setFieldValue(
                                            'uniqueVPN.addendumOnFile',
                                            selectedOption.value
                                        )
                                    }
                                />
                            </td>
                            {formik.touched['uniqueVPN.addendumOnFile'] &&
                            formik.errors['uniqueVPN.addendumOnFile'] ? (
                                <ErrorMessage
                                    message={
                                        formik.errors[
                                            'uniqueVPN.addendumOnFile'
                                        ]
                                    }
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="dentalSoftware">
                                    Dental Software:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="dentalSoftware"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('dentalSoftware')}
                                />
                            </td>
                            {formik.touched.dentalSoftware &&
                            formik.errors.dentalSoftware ? (
                                <ErrorMessage
                                    message={formik.errors.dentalSoftware}
                                />
                            ) : null}
                        </tr>

                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="dsURL">
                                    Dental Software URL:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="dsURL"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('dsURL')}
                                />
                            </td>
                            {formik.touched.dsURL && formik.errors.dsURL ? (
                                <ErrorMessage message={formik.errors.dsURL} />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="eClaims">eClaims:</label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="eClaims"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('eClaims')}
                                />
                            </td>
                            {formik.touched.eClaims && formik.errors.eClaims ? (
                                <ErrorMessage message={formik.errors.eClaims} />
                            ) : null}
                        </tr>

                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="eClaimsInstructions">
                                    eClaims Instructions:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="eClaimsInstructions"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps(
                                        'eClaimsInstructions'
                                    )}
                                />
                            </td>
                            {formik.touched.eClaimsInstructions &&
                            formik.errors.eClaimsInstructions ? (
                                <ErrorMessage
                                    message={formik.errors.eClaimsInstructions}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="attachments">
                                    Attachments:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="attachments"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('attachments')}
                                />
                            </td>
                            {formik.touched.attachments &&
                            formik.errors.attachments ? (
                                <ErrorMessage
                                    message={formik.errors.attachments}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="xrays">X-Rays:</label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="xrays"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('xrays')}
                                />
                            </td>
                            {formik.touched.xrays && formik.errors.xrays ? (
                                <ErrorMessage message={formik.errors.xrays} />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="pdfPrinter">PDF Printer:</label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="pdfPrinter"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('pdfPrinter')}
                                />
                            </td>
                            {formik.touched.pdfPrinter &&
                            formik.errors.pdfPrinter ? (
                                <ErrorMessage
                                    message={formik.errors.pdfPrinter}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="scannerInfo">
                                    Scanner Info:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="scannerInfo"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('scannerInfo')}
                                />
                            </td>
                            {formik.touched.scannerInfo &&
                            formik.errors.scannerInfo ? (
                                <ErrorMessage
                                    message={formik.errors.scannerInfo}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="backupTimes">
                                    Backup Times:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="backupTimes"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('backupTimes')}
                                />
                            </td>
                            {formik.touched.backupTimes &&
                            formik.errors.backupTimes ? (
                                <ErrorMessage
                                    message={formik.errors.backupTimes}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="firewallInfo">
                                    Firewall Info:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <input
                                    id="firewallInfo"
                                    type="text"
                                    className="office-computer-edit-input"
                                    {...formik.getFieldProps('firewallInfo')}
                                />
                            </td>
                            {formik.touched.firewallInfo &&
                            formik.errors.firewallInfo ? (
                                <ErrorMessage
                                    message={formik.errors.firewallInfo}
                                />
                            ) : null}
                        </tr>

                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="isKoc">Is Auth for KOC:</label>
                            </td>
                            <td className="office-table-right-cell">
                                <Select
                                    id="isKoc"
                                    name="isKoc"
                                    options={booleanOptions}
                                    value={booleanOptions.find(
                                        (option) =>
                                            option.value === formik.values.isKoc
                                    )}
                                    onChange={(selectedOption) =>
                                        formik.setFieldValue(
                                            'isKoc',
                                            selectedOption.value
                                        )
                                    }
                                />
                            </td>
                            {formik.touched.isKoc && formik.errors.isKoc ? (
                                <ErrorMessage message={formik.errors.isKoc} />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="isCopilotOffice">
                                    Is Copilot Office?:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <Select
                                    id="isCopilotOffice"
                                    name="isCopilotOffice"
                                    options={booleanOptions}
                                    value={booleanOptions.find(
                                        (option) =>
                                            option.value ===
                                            formik.values.isCopilotOffice
                                    )}
                                    onChange={(selectedOption) =>
                                        formik.setFieldValue(
                                            'isCopilotOffice',
                                            selectedOption.value
                                        )
                                    }
                                />
                            </td>
                            {formik.touched.isCopilotOffice &&
                            formik.errors.isCopilotOffice ? (
                                <ErrorMessage
                                    message={formik.errors.isCopilotOffice}
                                />
                            ) : null}
                        </tr>
                        <tr>
                            <td className="office-table-left-cell">
                                <label htmlFor="isCompanionOffice">
                                    Is Companion office?:
                                </label>
                            </td>
                            <td className="office-table-right-cell">
                                <Select
                                    id="isCompanionOffice"
                                    name="isCompanionOffice"
                                    options={booleanOptions}
                                    value={booleanOptions.find(
                                        (option) =>
                                            option.value ===
                                            formik.values.isCompanionOffice
                                    )}
                                    onChange={(selectedOption) =>
                                        formik.setFieldValue(
                                            'isCompanionOffice',
                                            selectedOption.value
                                        )
                                    }
                                />
                            </td>
                            {formik.touched.isCompanionOffice &&
                            formik.errors.isCompanionOffice ? (
                                <ErrorMessage
                                    message={formik.errors.isCompanionOffice}
                                />
                            ) : null}
                        </tr>
                        {!formik.values.hubId && (
                            <tr>
                                <td
                                    colSpan={2}
                                    style={{
                                        fontSize: 13,
                                        color: '#666',
                                        fontStyle: 'italic',
                                        padding: '.5em'
                                    }}>
                                    Office not linked to Hub2 — KOC notes
                                    won&apos;t sync.
                                </td>
                            </tr>
                        )}
                        {!formik.values.isCopilotOffice &&
                            formik.values.isCompanionOffice && (
                                <>
                                    <tr>
                                        <td className="office-table-left-cell">
                                            <label
                                                htmlFor="superSyncComputer"
                                                style={requiredLabelStyle(
                                                    isBlank(
                                                        formik.values
                                                            .superSyncComputer
                                                    )
                                                )}>
                                                Super Sync Computer:
                                            </label>
                                        </td>
                                        <td className="office-table-right-cell">
                                            <input
                                                id="superSyncComputer"
                                                type="text"
                                                className="office-computer-edit-input"
                                                {...formik.getFieldProps(
                                                    'superSyncComputer'
                                                )}
                                            />
                                        </td>
                                        {formik.touched.superSyncComputer &&
                                        formik.errors.superSyncComputer ? (
                                            <ErrorMessage
                                                message={
                                                    formik.errors
                                                        .superSyncComputer
                                                }
                                            />
                                        ) : null}
                                    </tr>
                                </>
                            )}
                        {formik.values.isCopilotOffice && (
                            <>
                                <tr>
                                    <td className="office-table-left-cell">
                                        <label
                                            htmlFor="copilotServerName"
                                            style={requiredLabelStyle(
                                                isCopilot &&
                                                    isBlank(
                                                        formik.values
                                                            .copilotServerName
                                                    )
                                            )}>
                                            Copilot Server Name:
                                        </label>
                                    </td>
                                    <td className="office-table-right-cell">
                                        <input
                                            id="copilotServerName"
                                            type="text"
                                            className="office-computer-edit-input"
                                            {...formik.getFieldProps(
                                                'copilotServerName'
                                            )}
                                        />
                                    </td>
                                    {formik.touched.copilotServerName &&
                                    formik.errors.copilotServerName ? (
                                        <ErrorMessage
                                            message={
                                                formik.errors.copilotServerName
                                            }
                                        />
                                    ) : null}
                                </tr>

                                <tr>
                                    <td className="office-table-left-cell">
                                        <label
                                            htmlFor="superSyncComputer"
                                            style={requiredLabelStyle(
                                                isCompanion &&
                                                    isBlank(
                                                        formik.values
                                                            .superSyncComputer
                                                    )
                                            )}>
                                            Super Sync Computer:
                                        </label>
                                    </td>
                                    <td className="office-table-right-cell">
                                        <input
                                            id="superSyncComputer"
                                            type="text"
                                            className="office-computer-edit-input"
                                            {...formik.getFieldProps(
                                                'superSyncComputer'
                                            )}
                                        />
                                    </td>
                                    {formik.touched.superSyncComputer &&
                                    formik.errors.superSyncComputer ? (
                                        <ErrorMessage
                                            message={
                                                formik.errors.superSyncComputer
                                            }
                                        />
                                    ) : null}
                                </tr>

                                <tr>
                                    <td className="office-table-left-cell">
                                        <label
                                            htmlFor="dxcAccountNumber"
                                            style={requiredLabelStyle(
                                                billingIncomplete
                                            )}>
                                            DXC Account #:
                                        </label>
                                    </td>
                                    <td className="office-table-right-cell">
                                        <input
                                            id="dxcAccountNumber"
                                            type="text"
                                            className="office-computer-edit-input"
                                            {...formik.getFieldProps(
                                                'dxcAccountNumber'
                                            )}
                                        />
                                    </td>
                                    {formik.touched.dxcAccountNumber &&
                                    formik.errors.dxcAccountNumber ? (
                                        <ErrorMessage
                                            message={
                                                formik.errors.dxcAccountNumber
                                            }
                                        />
                                    ) : null}
                                </tr>
                                <tr>
                                    <td className="office-table-left-cell">
                                        <label
                                            htmlFor="edsPracticeId"
                                            style={requiredLabelStyle(
                                                billingIncomplete ||
                                                    (isCopilot &&
                                                        !hasDxc &&
                                                        isBlank(
                                                            formik.values
                                                                .edsPracticeId
                                                        ))
                                            )}>
                                            EDS Practice ID:
                                        </label>
                                    </td>
                                    <td className="office-table-right-cell">
                                        <input
                                            id="edsPracticeId"
                                            type="text"
                                            className="office-computer-edit-input"
                                            {...formik.getFieldProps(
                                                'edsPracticeId'
                                            )}
                                        />
                                    </td>
                                    {formik.touched.edsPracticeId &&
                                    formik.errors.edsPracticeId ? (
                                        <ErrorMessage
                                            message={
                                                formik.errors.edsPracticeId
                                            }
                                        />
                                    ) : null}
                                </tr>

                                <tr>
                                    <td
                                        style={{
                                            borderRight: '1px #DCDCDC solid',
                                            padding: '.5em'
                                        }}>
                                        <label htmlFor="copilotNotes">
                                            Copilot Notes:
                                        </label>
                                    </td>
                                    <td style={{ padding: '.5em' }}>
                                        <textarea
                                            id="copilotNotes"
                                            className="office-computer-edit-input"
                                            style={{
                                                height: '10em',
                                                paddingTop: '.5em'
                                            }}
                                            {...formik.getFieldProps(
                                                'copilotNotes'
                                            )}
                                        />
                                    </td>
                                    {formik.touched.copilotNotes &&
                                    formik.errors.copilotNotes ? (
                                        <ErrorMessage
                                            message={formik.errors.copilotNotes}
                                        />
                                    ) : null}
                                </tr>
                            </>
                        )}
                        {formik.values.numOfPPConnections > 0 && <></>}
                    </tbody>
                </table>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-evenly'
                    }}>
                    <SubmitButton
                        label="Submit"
                        style={{ marginLeft: '30px' }}
                    />
                    <button
                        className="submit-btn"
                        onClick={
                            objectToUpdate
                                ? () => setShowEdit(false)
                                : () => navigate('/notes')
                        }>
                        Cancel
                    </button>
                </div>
                <div>{officeLoading && <LoadingSpinner />}</div>
            </form>
            <br />
            <br />
            {Object.keys(formik.errors).length !== 0 &&
            Object.keys(formik.touched).length !== 0 ? (
                <ErrorMessage message="There are form errors above, please fix." />
            ) : null}
            {officeError &&
            officeError.message !== 'Office Systematic Name already exists' ? (
                <ErrorMessage message={officeError.message} />
            ) : null}
            {office && <SuccessMessage message="Office successfully created" />}
        </>
    );
};

CreateUpdateOfficeForm.propTypes = {
    objectToUpdate: PropTypes.object,
    setShowEdit: PropTypes.func
};

export default CreateUpdateOfficeForm;
